import { supabase } from '../lib/supabaseClient';
import { debugLog } from '../utils/debugLog';

export interface LoginResult {
  success: boolean;
  user?: any;
  error?: string;
  requiresPasswordChange?: boolean;
}

const isNetworkError = (error: unknown): boolean => {
  if (!error) return false;

  const maybeError = error as { message?: string; details?: string; code?: string };
  const message = `${maybeError.message || ''} ${maybeError.details || ''}`.toLowerCase();

  return (
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network error') ||
    maybeError.code === ''
  );
};

const getConnectionErrorMessage = () =>
  'Veritabani baglantisi kurulamadi. Supabase proje URL/env ayarlarini kontrol edin.';

export const customAuthService = {
  // Custom login - Supabase Auth bypass
  async login(email: string, password: string): Promise<LoginResult> {
    try {
      const normalizedEmail = email.trim().toLowerCase();

      // 1. Profiles'dan kullanıcıyı bul
      let { data: user, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (error || !user) {
        if (isNetworkError(error)) {
          console.error('Supabase profil sorgusu baglanti hatasi:', error);
          return { success: false, error: getConnectionErrorMessage() };
        }

        // Eğer muratgulenc@ekostrateji.com ise otomatik oluştur
        if (normalizedEmail === 'muratgulenc@ekostrateji.com') {
          const newUserId = crypto.randomUUID();
          const { error: createError } = await supabase
            .from('profiles')
            .insert({
              id: newUserId,
              email: normalizedEmail,
              tam_adi: 'Murat Gülenç',
              rol: 'ekostrateji_super_admin',
              firma_adi: 'Ekostrateji',
              temp_password: false,
              first_login: false
            });
          
          if (createError) {
            if (isNetworkError(createError)) {
              return { success: false, error: getConnectionErrorMessage() };
            }
            console.error('Ekostrateji admin oluşturma hatası:', createError);
            return { success: false, error: 'Kullanıcı bulunamadı' };
          }
          
          // Yeni oluşturulan kullanıcıyı al
          const { data: newUser, error: newUserError } = await supabase
            .from('profiles')
            .select('*')
            .eq('email', normalizedEmail)
            .maybeSingle();
          
          if (isNetworkError(newUserError)) {
            console.error('Yeni admin profil sorgusu baglanti hatasi:', newUserError);
            return { success: false, error: getConnectionErrorMessage() };
          }

          if (newUser) {
            user = newUser;
          } else {
            return { success: false, error: 'Kullanıcı oluşturulamadı' };
          }
        } else {
          return { success: false, error: 'Kullanıcı bulunamadı' };
        }
      }

      // 2. Temp password kontrolü
      const { data: tempPasswords } = await supabase
        .from('temp_passwords')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_used', false)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1);



      let isValidPassword = false;

      // 3. Geçici şifre varsa kontrol et
      let usedTempPassword = false;
      if (tempPasswords && tempPasswords.length > 0) {
        const tempPass = tempPasswords[0];
        if (tempPass.temp_password === password) {
          isValidPassword = true;
          usedTempPassword = true;
          
          // Geçici şifreyi kullanıldı olarak işaretle
          await supabase
            .from('temp_passwords')
            .update({ is_used: true })
            .eq('id', tempPass.id);
        }
      }

      // 4. Normal şifre kontrolü (Supabase'den hash kontrol et)
      if (!isValidPassword) {
        try {
          // Supabase'den password_hash'i al
          if (user.password_hash) {
            try {
              const bcryptjs = await import('bcryptjs');
              const bcrypt = bcryptjs.default || bcryptjs;
              isValidPassword = await bcrypt.compare(password, user.password_hash);
            } catch (bcryptError) {
              console.warn('⚠️ Bcrypt karşılaştırma hatası:', bcryptError);
              // Fallback: basit string karşılaştırması (güvenli değil ama test için)
              isValidPassword = password === user.password_hash;
            }
          }
        } catch (e) {
          console.warn('⚠️ Şifre kontrolü hatası:', e);
        }
      }

      // 5. Fallback: localStorage'dan hash kontrol et (eski cihazlar için)
      if (!isValidPassword) {
        try {
          const userPasswords = JSON.parse(localStorage.getItem('user_passwords') || '{}');
          const storedHash = userPasswords[normalizedEmail] || userPasswords[email];
          if (storedHash) {
            try {
              const bcryptjs = await import('bcryptjs');
              const bcrypt = bcryptjs.default || bcryptjs;
              isValidPassword = await bcrypt.compare(password, storedHash);
            } catch (bcryptError) {
              console.warn('⚠️ Bcrypt karşılaştırma hatası:', bcryptError);
              // Fallback: basit string karşılaştırması
              isValidPassword = password === storedHash;
            }
          }
        } catch (e) {
          console.warn('⚠️ Şifre kontrolü hatası:', e);
        }
      }

      // 6. Basit şifre kontrolü (geçici çözüm - sadece test için)
      if (!isValidPassword) {
        // Test şifreleri
        const testPasswords = ['123456789', 'test123'];
        
        // Ekostrateji süper admin için özel şifre
        if (normalizedEmail === 'muratgulenc@ekostrateji.com' && password === 'Mg974212**') {
          isValidPassword = true;
        } else if (testPasswords.includes(password)) {
          isValidPassword = true;
        }
      }

      if (!isValidPassword) {
        return { success: false, error: 'Şifre hatalı' };
      }

      // 6. Session oluştur
      const sessionToken = this.generateToken(user);
      localStorage.setItem('custom_auth_token', sessionToken);
      
      // 7. Temp session oluştur (sidebar için)
      // role_permissions tablosundan gerçek permissions çek
      let permissions = [];
      try {
        const { data: rolePerms, error: rolePermsError } = await supabase
          .from('role_permissions')
          .select('permission_code, permissions(permission_name, module_name, description)')
          .eq('role_code', user.rol)
          .eq('is_active', true);
        
        if (!rolePermsError && rolePerms && rolePerms.length > 0) {
          permissions = rolePerms.map((rp: any) => ({
            permission_code: rp.permission_code,
            permission_name: rp.permissions?.permission_name || rp.permission_code,
            module_name: rp.permissions?.module_name || 'Diğer',
            description: rp.permissions?.description || ''
          }));
          debugLog('[customAuthService] role_permissions permissions yüklendi', {
            count: permissions.length,
            role: user.rol
          });
        } else {
          permissions = this.getFallbackPermissions(user.rol);
        }
      } catch (permError) {
        console.warn('[customAuthService] role_permissions okunamadı, fallback kullanılıyor', permError);
        permissions = this.getFallbackPermissions(user.rol);
      }
      
      // User'a permissions ekle
      const userWithPermissions = {
        ...user,
        permissions: permissions
      };
      localStorage.setItem('custom_auth_user', JSON.stringify(userWithPermissions));

      const tempSession = {
        user: { id: user.id, email: user.email },
        profile: user,
        userFirmas: [{
          firma_id: user.firma_id || 'default',
          firma_adi: user.firma_adi || 'Test Firma',
          role: user.rol,
          is_active: true
        }],
        permissions: permissions,
        expires_at: Date.now() + (30 * 24 * 60 * 60 * 1000)
      };
      localStorage.setItem('temp_user_session', JSON.stringify(tempSession));


      return { success: true, user, requiresPasswordChange: usedTempPassword };

    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Token oluştur
  generateToken(user: any): string {
    const payload = {
      userId: user.id,
      email: user.email,
      rol: user.rol,
      timestamp: Date.now()
    };
    return btoa(JSON.stringify(payload));
  },

  // Mevcut kullanıcıyı al
  getCurrentUser(): any | null {
    const userStr = localStorage.getItem('custom_auth_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Çıkış yap
  logout(): void {
    localStorage.removeItem('custom_auth_token');
    localStorage.removeItem('custom_auth_user');
    localStorage.removeItem('temp_user_session');
  },

  // Token kontrolü
  isAuthenticated(): boolean {
    const token = localStorage.getItem('custom_auth_token');
    return !!token;
  },

  // Fallback permissions
  getFallbackPermissions(role: string) {
    switch (role) {
      case 'ekostrateji_super_admin':
        return [
          { permission_code: 'admin_panel_access', permission_name: 'Yönetim Paneli Erişimi', module_name: 'Yönetim' },
          { permission_code: 'company_management', permission_name: 'Firma Yönetimi', module_name: 'Yönetim' },
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'gri_economic_update', permission_name: 'GRI Ekonomik Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_economic_view', permission_name: 'GRI Ekonomik Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_environmental_update', permission_name: 'GRI Çevresel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_environmental_view', permission_name: 'GRI Çevresel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_general_update', permission_name: 'GRI Genel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_general_view', permission_name: 'GRI Genel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_social_update', permission_name: 'GRI Sosyal Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_social_view', permission_name: 'GRI Sosyal Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_universal_update', permission_name: 'GRI Evrensel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_universal_view', permission_name: 'GRI Evrensel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_view', permission_name: 'GRI Görüntüleme', module_name: 'GRI' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'materiality_matrix_update', permission_name: 'Önemlilik Matrisi Güncelleme', module_name: 'Analiz' },
          { permission_code: 'materiality_matrix_view', permission_name: 'Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'profile_update', permission_name: 'Profil Güncelleme', module_name: 'Profil' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' },
          { permission_code: 'reports_create', permission_name: 'Raporlar Oluşturma', module_name: 'Raporlama' },
          { permission_code: 'reports_export', permission_name: 'Raporlar Dışa Aktarma', module_name: 'Raporlama' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'role_management', permission_name: 'Rol Yönetimi', module_name: 'Yönetim' },
          { permission_code: 'sasb_index_view', permission_name: 'SASB İndex Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'survey_create', permission_name: 'Anket Oluşturma', module_name: 'Anket' },
          { permission_code: 'survey_view', permission_name: 'Anket Görüntüleme', module_name: 'Anket' },
          { permission_code: 'topic_assignment_update', permission_name: 'Konu Atama Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_update', permission_name: 'Konu Seçimi Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'tsrs_emissions_update', permission_name: 'TSRS Emisyonlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_emissions_view', permission_name: 'TSRS Emisyonlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_entities_update', permission_name: 'TSRS Kuruluşlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_entities_view', permission_name: 'TSRS Kuruluşlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_goals_update', permission_name: 'TSRS Amaçlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_goals_view', permission_name: 'TSRS Amaçlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_opportunities_update', permission_name: 'TSRS Fırsatlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_opportunities_view', permission_name: 'TSRS Fırsatlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_risks_update', permission_name: 'TSRS Riskler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_risks_view', permission_name: 'TSRS Riskler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_skills_update', permission_name: 'TSRS Beceriler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_skills_view', permission_name: 'TSRS Beceriler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_targets_update', permission_name: 'TSRS Hedefler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_targets_view', permission_name: 'TSRS Hedefler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_valuechain_update', permission_name: 'TSRS Değer Zinciri Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_valuechain_view', permission_name: 'TSRS Değer Zinciri Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'user_management', permission_name: 'Kullanıcı Yönetimi', module_name: 'Yönetim' }
        ];
      case 'firma_super_admin':
        return [
          { permission_code: 'admin_panel_access', permission_name: 'Yönetim Paneli Erişimi', module_name: 'Yönetim' },
          { permission_code: 'company_management', permission_name: 'Firma Yönetimi', module_name: 'Yönetim' },
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'firma_selection', permission_name: 'Firma Seçimi', module_name: 'Yönetim' },
          { permission_code: 'gri_economic_update', permission_name: 'GRI Ekonomik Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_economic_view', permission_name: 'GRI Ekonomik Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_environmental_update', permission_name: 'GRI Çevresel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_environmental_view', permission_name: 'GRI Çevresel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_general_update', permission_name: 'GRI Genel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_general_view', permission_name: 'GRI Genel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_social_update', permission_name: 'GRI Sosyal Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_social_view', permission_name: 'GRI Sosyal Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_universal_update', permission_name: 'GRI Evrensel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_universal_view', permission_name: 'GRI Evrensel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_view', permission_name: 'GRI Görüntüleme', module_name: 'GRI' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'materiality_matrix_update', permission_name: 'Önemlilik Matrisi Güncelleme', module_name: 'Analiz' },
          { permission_code: 'materiality_matrix_view', permission_name: 'Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'profile_update', permission_name: 'Profil Güncelleme', module_name: 'Profil' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' },
          { permission_code: 'reports_create', permission_name: 'Raporlar Oluşturma', module_name: 'Raporlama' },
          { permission_code: 'reports_export', permission_name: 'Raporlar Dışa Aktarma', module_name: 'Raporlama' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'sasb_index_view', permission_name: 'SASB İndex Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'survey_create', permission_name: 'Anket Oluşturma', module_name: 'Anket' },
          { permission_code: 'survey_view', permission_name: 'Anket Görüntüleme', module_name: 'Anket' },
          { permission_code: 'topic_assignment_update', permission_name: 'Konu Atama Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_update', permission_name: 'Konu Seçimi Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'tsrs_emissions_update', permission_name: 'TSRS Emisyonlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_emissions_view', permission_name: 'TSRS Emisyonlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_entities_update', permission_name: 'TSRS Kuruluşlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_entities_view', permission_name: 'TSRS Kuruluşlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_goals_update', permission_name: 'TSRS Amaçlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_goals_view', permission_name: 'TSRS Amaçlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_opportunities_update', permission_name: 'TSRS Fırsatlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_opportunities_view', permission_name: 'TSRS Fırsatlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_risks_update', permission_name: 'TSRS Riskler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_risks_view', permission_name: 'TSRS Riskler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_skills_update', permission_name: 'TSRS Beceriler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_skills_view', permission_name: 'TSRS Beceriler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_targets_update', permission_name: 'TSRS Hedefler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_targets_view', permission_name: 'TSRS Hedefler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_valuechain_update', permission_name: 'TSRS Değer Zinciri Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_valuechain_view', permission_name: 'TSRS Değer Zinciri Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'user_management', permission_name: 'Kullanıcı Yönetimi', module_name: 'Yönetim' }
        ];
      case 'firma_admin':
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'gri_economic_update', permission_name: 'GRI Ekonomik Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_economic_view', permission_name: 'GRI Ekonomik Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_environmental_update', permission_name: 'GRI Çevresel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_environmental_view', permission_name: 'GRI Çevresel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_general_update', permission_name: 'GRI Genel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_general_view', permission_name: 'GRI Genel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_social_update', permission_name: 'GRI Sosyal Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_social_view', permission_name: 'GRI Sosyal Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_universal_update', permission_name: 'GRI Evrensel Güncelleme', module_name: 'GRI' },
          { permission_code: 'gri_universal_view', permission_name: 'GRI Evrensel Görüntüleme', module_name: 'GRI' },
          { permission_code: 'gri_view', permission_name: 'GRI Görüntüleme', module_name: 'GRI' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'materiality_matrix_update', permission_name: 'Önemlilik Matrisi Güncelleme', module_name: 'Analiz' },
          { permission_code: 'materiality_matrix_view', permission_name: 'Önemlilik Matrisi Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'profile_update', permission_name: 'Profil Güncelleme', module_name: 'Profil' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' },
          { permission_code: 'reports_create', permission_name: 'Raporlar Oluşturma', module_name: 'Raporlama' },
          { permission_code: 'reports_export', permission_name: 'Raporlar Dışa Aktarma', module_name: 'Raporlama' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'sasb_index_view', permission_name: 'SASB İndex Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'survey_create', permission_name: 'Anket Oluşturma', module_name: 'Anket' },
          { permission_code: 'survey_view', permission_name: 'Anket Görüntüleme', module_name: 'Anket' },
          { permission_code: 'topic_assignment_update', permission_name: 'Konu Atama Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_assignment_view', permission_name: 'Konu Atama Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_update', permission_name: 'Konu Seçimi Güncelleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'topic_selection_view', permission_name: 'Konu Seçimi Görüntüleme', module_name: 'Konu Yönetimi' },
          { permission_code: 'tsrs_emissions_update', permission_name: 'TSRS Emisyonlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_emissions_view', permission_name: 'TSRS Emisyonlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_entities_update', permission_name: 'TSRS Kuruluşlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_entities_view', permission_name: 'TSRS Kuruluşlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_goals_update', permission_name: 'TSRS Amaçlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_goals_view', permission_name: 'TSRS Amaçlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_opportunities_update', permission_name: 'TSRS Fırsatlar Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_opportunities_view', permission_name: 'TSRS Fırsatlar Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_risks_update', permission_name: 'TSRS Riskler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_risks_view', permission_name: 'TSRS Riskler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_skills_update', permission_name: 'TSRS Beceriler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_skills_view', permission_name: 'TSRS Beceriler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_targets_update', permission_name: 'TSRS Hedefler Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_targets_view', permission_name: 'TSRS Hedefler Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_valuechain_update', permission_name: 'TSRS Değer Zinciri Güncelleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_valuechain_view', permission_name: 'TSRS Değer Zinciri Görüntüleme', module_name: 'TSRS' },
          { permission_code: 'tsrs_view', permission_name: 'TSRS Görüntüleme', module_name: 'Raporlama' },
          { permission_code: 'user_management', permission_name: 'Kullanıcı Yönetimi', module_name: 'Yönetim' }
        ];
      case 'bolum_kullanicisi':
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'data_collection_update', permission_name: 'Veri Toplama Güncelleme', module_name: 'Veri Toplama' },
          { permission_code: 'data_collection_view', permission_name: 'Veri Toplama Görüntüleme', module_name: 'Veri Toplama' },
          { permission_code: 'knowledge_base_view', permission_name: 'Bilgi Bankası Görüntüleme', module_name: 'Bilgi' },
          { permission_code: 'performance_indicators_view', permission_name: 'Performans Göstergeleri Görüntüleme', module_name: 'Analiz' },
          { permission_code: 'profile_update', permission_name: 'Profil Güncelleme', module_name: 'Profil' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' },
          { permission_code: 'reports_view', permission_name: 'Raporlar Görüntüleme', module_name: 'Raporlama' }
        ];
      default:
        return [
          { permission_code: 'dashboard_view', permission_name: 'Dashboard Görüntüleme', module_name: 'Dashboard' },
          { permission_code: 'profile_view', permission_name: 'Profil Görüntüleme', module_name: 'Profil' },
          { permission_code: 'profile_update', permission_name: 'Profil Güncelleme', module_name: 'Profil' }
        ];
    }
  }
};
